// Camera capture. Per the brief: request the rear camera once and reuse the
// stream (iOS WebKit doesn't persist PWA camera permission and misbehaves on
// re-requests). Grab a single still frame to a canvas for OpenCV processing.
let stream: MediaStream | null = null;

export async function getStream(): Promise<MediaStream> {
  if (stream && stream.active) return stream;
  stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: 'environment' }, width: { ideal: 2560 }, height: { ideal: 1920 } },
    audio: false,
  });
  return stream;
}

export function stopStream() {
  stream?.getTracks().forEach((t) => t.stop());
  stream = null;
}

/** Downscale a video frame to an ImageData (RGBA) with longest side <= maxSide.
 *  2000px matches the resolution the OMR engine was validated at — enough pixels
 *  for the small corner markers to survive detection on a phone capture. */
export function frameToImageData(video: HTMLVideoElement, maxSide = 2000): ImageData {
  const vw = video.videoWidth || 1280;
  const vh = video.videoHeight || 960;
  const scale = Math.min(1, maxSide / Math.max(vw, vh));
  const w = Math.round(vw * scale);
  const h = Math.round(vh * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(video, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

/**
 * Best-quality still from the live camera. Mobile browsers hand out a
 * low-resolution *preview* stream (often 640x480/720p) regardless of the
 * requested constraints, so a frame grabbed off the <video> is too coarse to
 * read faint pencil marks. ImageCapture.takePhoto() pulls a full-sensor photo
 * (Android Chrome); where it's unavailable (iOS Safari) we fall back to the
 * preview frame — there the "Use a photo instead" file capture is preferable.
 */
export async function captureStill(video: HTMLVideoElement, stream: MediaStream | null, maxSide = 2000): Promise<ImageData> {
  const track = stream?.getVideoTracks?.()[0];
  const IC = (window as unknown as { ImageCapture?: new (t: MediaStreamTrack) => { takePhoto: () => Promise<Blob> } }).ImageCapture;
  if (track && IC) {
    try {
      const blob = await new IC(track).takePhoto();
      return await fileToImageData(blob, maxSide);
    } catch {
      /* takePhoto not really supported / failed — use the preview frame */
    }
  }
  return frameToImageData(video, maxSide);
}

/** Decode an image Blob/File to a downscaled ImageData (camera fallback / desktop). */
export async function fileToImageData(file: Blob, maxSide = 2000): Promise<ImageData> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error('Could not read image'));
      im.src = url;
    });
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
    ctx.drawImage(img, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Render an ImageData (e.g. the engine's overlay) into a canvas element. */
export function drawImageData(canvas: HTMLCanvasElement, img: { data: Uint8ClampedArray; width: number; height: number }) {
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d')!;
  ctx.putImageData(new ImageData(new Uint8ClampedArray(img.data), img.width, img.height), 0, 0);
}
